import { useEffect, useRef, useState } from "react";
import {
    IoChevronDown,
    IoCloudUploadOutline,
    IoClose,
} from "react-icons/io5";
import { useNavigate, useLocation } from "react-router-dom";
import { whtsappIcon } from "../../asset";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../store";
import {
    fetchWhatsappSendList,
    sendWhatsappTemplate,
    sendWhatsappBulkTemplate,
    scheduleWhatsappBulkTemplate,
} from "../../features/template/TemplateService";
import WhatsappPreview from "../template/WhtsappPreview";
import { toast } from "sonner";
import PhoneInput from "react-phone-number-input";
import { isValidPhoneNumber } from "react-phone-number-input";
import ScheduleTimeModal from "./Scheduletimemodal";
import { getAuthUser } from "../../util/manageAuth";
import { uploadFile } from "../../util/imageUpload";

interface SendMsgComponentProps {
    mode: "single" | "bulk";
}

const mapButtonType = (apiType: string): "Quick Reply" | "URL" | "Call to Action" => {
    switch (apiType?.toUpperCase()) {
        case "QUICK_REPLY": return "Quick Reply";
        case "URL": return "URL";
        case "PHONE_NUMBER": return "Call to Action";
        default: return "Quick Reply";
    }
};

const mapButtons = (rawButtons: any[]) =>
    (rawButtons || []).map((btn: any, index: number) => ({
        id: String(index),
        type: mapButtonType(btn.type),
        text: btn.text || "",
        url: btn.url || undefined,
        phoneNumber: btn.phone_number || undefined,
    }));

export default function SendMsgComponent({ mode }: SendMsgComponentProps) {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch<AppDispatch>();

    const isReturningFromGallery = useRef(false);

    const isBulk = mode === "bulk";

    const [templates, setTemplates] = useState<any[]>([]);
    const [openTemplate, setOpenTemplate] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState<any>(null);

    const [phoneNumber, setPhoneNumber] = useState<string | undefined>();
    const [phoneNumbers, setPhoneNumbers] = useState("");

    const [selectedLanguage, setSelectedLanguage] = useState<string>("en_US");
    const [isLoading, setIsLoading] = useState(false);
    const [uploadedFile, setUploadedFile] = useState<File | null>(null);

    // ── Media for header preview ──
    const [headerMediaFile, setHeaderMediaFile] = useState<File | null>(null);
    const [headerMediaPreviewUrl, setHeaderMediaPreviewUrl] = useState<string>("");

    const [headerMediaUploadedUrl, setHeaderMediaUploadedUrl] = useState<string | undefined>();
    const [headerMediaUploading, setHeaderMediaUploading] = useState(false);

    // Selected customers from CustomerList picker
    const [selectedCustomerIds, setSelectedCustomerIds] = useState<number[]>([]);
    const [selectedCustomerNames, setSelectedCustomerNames] = useState<string[]>([]);

    // Schedule state
    const [showTimePicker, setShowTimePicker] = useState(false);
    const [scheduleTimeLabel, setScheduleTimeLabel] = useState<string>("");
    const [scheduleDate, setScheduleDate] = useState<string>("");
    const [scheduleTime, setScheduleTime] = useState<string>("");
    const [scheduleType, setScheduleType] = useState<"ONCE" | "DAILY">("ONCE");

    const [formState, setFormState] = useState({
        phoneNumber: "",
        phoneNumbers: "",
        selectedTemplate: null as any,
        selectedLanguage: "en_US",
        uploadedFileName: "",
    });
    useEffect(() => {
        if (!location.state) return;

        const saved = sessionStorage.getItem("sendMsgForm");
        if (saved) {
            const parsed = JSON.parse(saved);

            setFormState(parsed);
            setPhoneNumber(parsed.phoneNumber);
            setPhoneNumbers(parsed.phoneNumbers);
            setSelectedTemplate(parsed.selectedTemplate);
            setSelectedLanguage(parsed.selectedLanguage);
        }
    }, []);

    /* ── Revoke blob URL on cleanup to avoid memory leaks ── */
    useEffect(() => {
        return () => {
            if (headerMediaPreviewUrl) URL.revokeObjectURL(headerMediaPreviewUrl);
        };
    }, [headerMediaPreviewUrl]);

    /* ── When template changes, clear the previous media ── */
    useEffect(() => {
        if (!selectedTemplate) return;
        if (isReturningFromGallery.current) {
            isReturningFromGallery.current = false;
            return;
        }
        setHeaderMediaFile(null);
        setHeaderMediaPreviewUrl("");
        setHeaderMediaUploadedUrl(undefined);
    }, [selectedTemplate?.templateName]);

    /* ── Listen for customers selected from picker ── */
    useEffect(() => {
        if (location.state?.selectedCustomerIds) {
            setSelectedCustomerIds(location.state.selectedCustomerIds);
            setSelectedCustomerNames(location.state.selectedCustomerNames || []);

            // clear state so it doesn't persist on refresh
            navigate(location.pathname, { replace: true });
        }
    }, [location.state]);

    /* ── Listen for gallery media selection ── */
    useEffect(() => {
        if (location.state?.selectedMedia) {
            const media = location.state.selectedMedia;

            isReturningFromGallery.current = true;

            setHeaderMediaPreviewUrl(media.mediaUrl);
            setHeaderMediaFile(null);
            setHeaderMediaUploadedUrl(media.mediaUrl);
            
            setHeaderMediaUploading(false);

            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.state]);

    useEffect(() => {
        sessionStorage.setItem("sendMsgForm", JSON.stringify(formState));
    }, [formState]);

    /* -------- FETCH TEMPLATE LIST -------- */
    useEffect(() => {
        const loadTemplates = async () => {
            try {
                const res = await dispatch(fetchWhatsappSendList({ size: 25 })).unwrap();
                setTemplates(res?.results || []);
            } catch (err) {
                console.error(err);
            }
        };
        loadTemplates();
    }, [dispatch]);

    const handleHeaderMediaChange = async (file: File) => {
        if (headerMediaPreviewUrl) URL.revokeObjectURL(headerMediaPreviewUrl);
        setHeaderMediaFile(file);
        setHeaderMediaPreviewUrl(URL.createObjectURL(file));
        setHeaderMediaUploadedUrl(undefined);

        // call create galery

        try {
            setHeaderMediaUploading(true);
            const url = await uploadFile(file);
            setHeaderMediaUploadedUrl(url);
        } catch (err: any) {
            toast.error(err.message || "Failed to upload header media");
            setHeaderMediaFile(null);
            setHeaderMediaPreviewUrl("");
        } finally {
            setHeaderMediaUploading(false);
        }
    };

    /* ── HANDLE SEND ── */
    const handleSend = async () => {
        if (!selectedTemplate) { toast.info("Please select a template"); return; }
        if (!selectedLanguage) { toast.info("Please select a language"); return; }
        if (hasMediaHeader && !(headerMediaFile || headerMediaUploadedUrl)) {
            toast.error("Please upload a header media file");
            return;
        }
        const headerType = selectedTemplate?.headerType?.toUpperCase();

        try {
            setIsLoading(true);

            // CHANGE 4: use already-uploaded URL; guard if still uploading
            if (hasMediaHeader && headerMediaUploading) {
                toast.error("Please wait for the media to finish uploading");
                return;
            }

            setIsLoading(true);
            const uploadedHeaderMediaUrl = headerMediaUploadedUrl;

            if (isBulk) {
                const manualNumbers = phoneNumbers
                    .split(/[,\s\n]+/)
                    .map((n) => n.trim())
                    .filter(Boolean);

                const hasCustomers = selectedCustomerIds.length > 0;
                const hasNumbers = manualNumbers.length > 0;
                const hasFile = !!uploadedFile;

                if (!hasCustomers && !hasNumbers && !hasFile) {
                    toast.error("Select customers, enter phone numbers, or upload a file");
                    return;
                }

                if (scheduleDate && scheduleTime) {
                    await dispatch(
                        scheduleWhatsappBulkTemplate({
                            templateName: selectedTemplate.templateName,
                            language: selectedLanguage,
                            headerMediaId: uploadedHeaderMediaUrl,
                            headerParams: [],
                            bodyParams: [],
                            buttonUrlParams: [],
                            customerIds: selectedCustomerIds,
                            mobileNumbers: manualNumbers,
                            file: uploadedFile,
                            scheduleDate,
                            scheduleTime,
                            scheduleType,
                            headerType
                        })
                    ).unwrap();
                } else {
                    await dispatch(
                        sendWhatsappBulkTemplate({
                            templateName: selectedTemplate.templateName,
                            language: selectedLanguage,
                            headerMediaId: uploadedHeaderMediaUrl,
                            headerParams: [],
                            bodyParams: [],
                            buttonUrlParams: [],
                            customerIds: selectedCustomerIds,
                            mobileNumbers: manualNumbers,
                            file: uploadedFile,
                            headerType,
                        })
                    ).unwrap();
                }
            } else {
                if (!phoneNumber || !isValidPhoneNumber(phoneNumber)) {
                    toast.error("Enter a valid phone number");
                    return;
                }

                await dispatch(
                    sendWhatsappTemplate({
                        to: phoneNumber.replace("+", ""),
                        templateName: selectedTemplate.templateName,
                        language: selectedLanguage,
                        headerParams: [],
                        bodyParams: [],
                        headerMediaId: uploadedHeaderMediaUrl,
                        buttonUrlParams: [],
                        headerType,
                    })
                ).unwrap();
            }

            toast.success(scheduleDate && scheduleTime ? "Message scheduled successfully!" : "Message sent successfully!");
            sessionStorage.removeItem("sendMsgForm");

            setPhoneNumber(undefined);
            setPhoneNumbers("");
            setSelectedTemplate(null);
            setSelectedLanguage("en_US");
            setUploadedFile(null);
            setHeaderMediaFile(null);
            setHeaderMediaPreviewUrl("");
            setHeaderMediaUploadedUrl(undefined);
            setSelectedCustomerIds([]);
            setSelectedCustomerNames([]);
            setScheduleTimeLabel("");
            setScheduleDate("");
            setScheduleTime("");
            setScheduleType("ONCE");

        } catch (error: any) {
            const errMsg =
                typeof error === "string"
                    ? error
                    : error?.message || error?.field || "Failed to send message";
            toast.error(errMsg);
        } finally {
            setIsLoading(false);
        }
    };

    const removeSelectedCustomer = (id: number) => {
        setSelectedCustomerIds((prev) => prev.filter((c) => c !== id));
        setSelectedCustomerNames((prev) =>
            prev.filter((_, i) => selectedCustomerIds[i] !== id)
        );
    };

    // Determine if the selected template has a media header
    const headerType = selectedTemplate?.headerType?.toUpperCase();
    const hasMediaHeader = ["IMAGE", "VIDEO", "DOCUMENT"].includes(headerType);

    return (
        <div className="flex md:flex-row flex-col gap-2 bg-white rounded-4xl p-6">
            <div className="md:w-3/5 w-full flex flex-col gap-6 border rounded-4xl shadow-lg p-6">

                {/* ── Top bar ── */}
                <div className="flex items-center justify-between">
                    <span className="text-lg font-semibold text-gray-800">Message Details</span>

                    {/* {isBulk && (
                        <div className="flex items-center gap-2">
                            {scheduleTimeLabel && (
                                <span className="flex items-center gap-1 text-xs bg-blue-50 text-blue-700 font-semibold px-3 py-1 rounded-full">
                                    {scheduleTimeLabel}
                                    <IoClose
                                        className="cursor-pointer hover:text-red-500"
                                        onClick={() => {
                                            setScheduleTimeLabel("");
                                            setScheduleDate("");
                                            setScheduleTime("");
                                            setScheduleType("ONCE");
                                        }}
                                    />
                                </span>
                            )}
                            <button
                                onClick={() => setShowTimePicker(true)}
                                disabled={isLoading || headerMediaUploading} // ✅ add this
                                className="flex items-center text-sm gap-2 px-4 py-2 rounded-full border font-semibold transition bg-brand-blue-gradient text-white"
                            >
                                {scheduleTimeLabel ? "Change Time" : "Schedule Send"}
                            </button>
                        </div>
                    )} */}
                </div>

                {/* ── Sender ── */}
                <div className="flex flex-col gap-2">
                    <label className="text-sm font-bold">Sender's Whatsapp Number</label>
                    <div className="flex items-center justify-between border rounded-xl px-4 py-3 cursor-pointer hover:border-gray-400 transition">
                        <div className="flex items-center gap-3">
                            <img src={whtsappIcon} className="w-8 h-8" />
                            <div className="flex flex-col">
                                <span className="font-semibold text-gray-800">Empedance.com</span>
                                <span className="text-sm text-gray-500">
                                    {(getAuthUser()?.mobile || "Not Connected").replace("+", "")}
                                    <span className="text-green-600 font-semibold ml-2">• CONNECTED</span>
                                </span>
                            </div>
                        </div>
                        <IoChevronDown className="text-gray-500" />
                    </div>
                </div>

                {/* SINGLE */}
                {!isBulk && (
                    <div className="border rounded-xl px-4 py-2 focus-within:border-brand-main">
                        <PhoneInput
                            defaultCountry="IN"
                            limitMaxLength
                            value={phoneNumber}
                            onChange={(val) => {
                                setPhoneNumber(val);
                                setFormState(prev => ({ ...prev, phoneNumber: val || "" }));
                            }}
                            className="phone-input focus:outline-none"
                        />
                    </div>
                )}

                {/* BULK */}
                {isBulk && (
                    <>
                        {/* Select Customers */}
                        <div className="flex flex-col gap-3">
                            <button
                                onClick={() =>
                                    navigate("/admin/customers?mode=select", {
                                        state: { returnTo: `/send-message/${mode}` },
                                    })
                                }
                                className="bg-brand-main text-white rounded-xl py-3 font-semibold hover:opacity-90 transition"
                            >
                                Select Customers
                                {selectedCustomerIds.length > 0 && (
                                    <span className="ml-2 bg-white text-brand-main text-xs font-bold px-2 py-0.5 rounded-full">
                                        {selectedCustomerIds.length}
                                    </span>
                                )}
                            </button>

                            {/* Selected customer chips */}
                            {selectedCustomerNames.length > 0 && (
                                <div className="flex flex-wrap gap-2">
                                    {selectedCustomerNames.map((name, i) => (
                                        <span
                                            key={selectedCustomerIds[i]}
                                            className="flex items-center gap-1 bg-blue-50 text-blue-700 text-xs font-semibold px-3 py-1 rounded-full"
                                        >
                                            {name}
                                            <IoClose
                                                className="cursor-pointer hover:text-red-500"
                                                onClick={() => removeSelectedCustomer(selectedCustomerIds[i])}
                                            />
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>

                        <Divider />

                        {/* Manual numbers */}
                        <div className="flex flex-col gap-2">
                            <label className="text-sm font-bold">Enter Multiple Phone Numbers</label>
                            <textarea
                                rows={5}
                                placeholder={`Enter numbers separated by comma, space or new line\ne.g. +919876543210, +971502049838`}
                                value={phoneNumbers}
                                onChange={(e) => {
                                    setPhoneNumbers(e.target.value);
                                    setFormState(prev => ({ ...prev, phoneNumbers: e.target.value }));
                                }}
                                className="border rounded-xl px-4 py-3 resize-none outline-none focus:border-brand-main text-sm"
                            />
                            {phoneNumbers.trim() && (
                                <span className="text-xs text-gray-400">
                                    {phoneNumbers.split(/[,\s\n]+/).filter((n) => n.trim()).length} number(s) entered
                                </span>
                            )}
                        </div>

                        <Divider />

                        {/* File Upload */}
                        <div className="border-2 border-dashed rounded-2xl p-6 text-center cursor-pointer hover:border-brand-main transition relative">
                            <input
                                type="file"
                                accept=".xlsx,.xls,.csv"
                                className="absolute inset-0 opacity-0 cursor-pointer"
                                onChange={(e) => {
                                    const file = e.target.files?.[0] ?? null;
                                    setUploadedFile(file);

                                    if (file) {
                                        setFormState(prev => ({
                                            ...prev,
                                            uploadedFileName: file.name
                                        }));
                                    }
                                }}
                            />
                            <div className="flex flex-col items-center gap-2 text-gray-500">
                                <IoCloudUploadOutline className="text-3xl" />
                                {uploadedFile ? (
                                    <span className="font-semibold text-brand-main">{uploadedFile.name}</span>
                                ) : formState.uploadedFileName ? (
                                    <span className="text-gray-400 text-sm">
                                        Previously selected: {formState.uploadedFileName}
                                    </span>
                                ) : (
                                    <>
                                        <span className="font-semibold">Click to upload or drag and drop</span>
                                        <span className="text-sm">XLSX, XLS, CSV files</span>
                                    </>
                                )}
                            </div>
                        </div>
                    </>
                )}

                {/* Template Dropdown */}
                <div className="flex flex-col gap-2 relative">
                    <label className="text-sm font-bold">Template</label>
                    <div
                        onClick={() => setOpenTemplate(!openTemplate)}
                        className="flex items-center justify-between border rounded-xl px-4 py-3 cursor-pointer hover:border-gray-400 transition"
                    >
                        <span className="text-gray-700">
                            {selectedTemplate?.templateName || "Select Template"}
                        </span>
                        <IoChevronDown />
                    </div>
                    {openTemplate && (
                        <div className="absolute top-full mt-2 w-full bg-white border rounded-xl shadow-lg z-20 max-h-60 overflow-y-auto">
                            {templates.map((item: any) => (
                                <div
                                    key={item.id}
                                    onClick={() => {
                                        const headerComponent = item.components?.find((c: any) => c.type === "HEADER");
                                        const bodyComponent = item.components?.find((c: any) => c.type === "BODY");
                                        const footerComponent = item.components?.find((c: any) => c.type === "FOOTER");
                                        const buttonsComponent = item.components?.find((c: any) => c.type === "BUTTONS");

                                        const selected = {
                                            templateName: item.name,
                                            headerType: headerComponent?.format || null,
                                            headerText: headerComponent?.text || null,
                                            headerImageUrl: headerComponent?.example?.header_handle?.[0] || null,
                                            bodyText: bodyComponent?.text || null,
                                            footerText: footerComponent?.text || null,
                                            buttons: mapButtons(buttonsComponent?.buttons),
                                        };

                                        setSelectedTemplate(selected);

                                        setFormState(prev => ({
                                            ...prev,
                                            selectedTemplate: selected
                                        }));

                                        setOpenTemplate(false);
                                        setOpenTemplate(false);
                                    }}
                                    className="px-4 py-3 hover:bg-gray-100 cursor-pointer text-sm font-semibold"
                                >
                                    {item.name}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* ── Header Media Upload (only shown when template has IMAGE/VIDEO/DOCUMENT header) ── */}
                {hasMediaHeader && !(headerMediaFile || headerMediaPreviewUrl) && (
                    <>
                        {/* Choose from Gallery */}
                        <button
                            onClick={() => {
                                navigate("/gallery", {
                                    state: {
                                        mode: "select",
                                        returnTo: `/send-message/${mode}`
                                    }
                                });
                            }}
                            className="w-full px-4 py-2 bg-brand-blue-gradient text-white border rounded-xl text-sm font-semibold hover:bg-gray-50 transition"
                        >
                            Choose from Gallery
                        </button>

                        <Divider />

                        {/* Upload */}
                        <div className="border-2 border-dashed rounded-2xl p-5 flex flex-col items-center gap-2 cursor-pointer relative">
                            <input
                                type="file"
                                accept={
                                    headerType === "IMAGE" ? "image/*"
                                        : headerType === "VIDEO" ? "video/*"
                                            : ".pdf,.doc,.docx"
                                }
                                className="absolute inset-0 opacity-0 cursor-pointer"
                                onChange={(e) => {
                                    const f = e.target.files?.[0];
                                    if (f) handleHeaderMediaChange(f);
                                }}
                            />
                            <IoCloudUploadOutline className="text-3xl text-gray-400" />
                            <span className="text-sm text-gray-400">
                                Click to upload {headerType.toLowerCase()}
                            </span>
                        </div>
                    </>
                )}

                {/* Language */}
                <div className="w-48">
                    <Dropdown
                        label="Language"
                        placeholder="Select"
                        value={selectedLanguage}
                        onChange={setSelectedLanguage}
                        options={[{ label: "English", value: "en_US" }]}
                    />
                </div>

                {/* Send Button */}
                <div className="flex justify-end">
                    <button
                        onClick={handleSend}
                        disabled={isLoading}
                        className="bg-brand-main text-white rounded-xl px-8 py-3 font-semibold hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isLoading
                            ? scheduleDate && scheduleTime ? "Scheduling..." : "Sending..."
                            : scheduleDate && scheduleTime ? "Schedule Message" : "Send Message"
                        }
                    </button>
                </div>
            </div>

            <div className="md:w-2/5 w-full">
                <WhatsappPreview
                    templateData={selectedTemplate || {}}
                    buttons={selectedTemplate?.buttons || []}
                    mediaPreviewUrl={headerMediaPreviewUrl || undefined}
                    mediaFile={headerMediaFile}
                />
            </div>

            {/* Schedule Time Picker Modal */}
            {showTimePicker && (
                <ScheduleTimeModal
                    onConfirm={(label, date, time, type) => {
                        setScheduleTimeLabel(label);
                        setScheduleDate(date);
                        setScheduleTime(time);
                        setScheduleType(type);
                        setShowTimePicker(false);
                    }}
                    onClose={() => setShowTimePicker(false)}
                />
            )}
        </div>
    );
}

function Divider() {
    return (
        <div className="flex items-center gap-3 text-gray-400 text-sm">
            <div className="flex-1 border-t" />OR<div className="flex-1 border-t" />
        </div>
    );
}

function Dropdown({
    label,
    placeholder,
    value,
    onChange,
    options = [],
}: {
    label: string;
    placeholder: string;
    value?: string;
    onChange?: (value: string) => void;
    options?: { label: string; value: string }[];
}) {
    const [isOpen, setIsOpen] = useState(false);
    const selectedOption = options.find((opt) => opt.value === value);

    return (
        <div className="flex flex-col gap-2 relative">
            <label className="text-sm font-bold">{label}</label>
            <div
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center justify-between border rounded-xl px-4 py-3 cursor-pointer hover:border-gray-400 transition"
            >
                <span className={selectedOption ? "text-gray-700" : "text-gray-500"}>
                    {selectedOption?.label || placeholder}
                </span>
                <IoChevronDown />
            </div>
            {isOpen && options.length > 0 && (
                <div className="absolute top-full mt-2 w-full bg-white border rounded-xl shadow-lg z-20 max-h-60 overflow-y-auto">
                    {options.map((option) => (
                        <div
                            key={option.value}
                            onClick={() => { onChange?.(option.value); setIsOpen(false); }}
                            className="px-4 py-3 hover:bg-gray-100 cursor-pointer text-sm font-semibold"
                        >
                            {option.label}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}